import { Box, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { t } from '../constants/translations';
import { useAdmin } from '../contexts/admin.context';
import { useCafe } from '../contexts/cafe.context';
import { useLocale } from '../contexts/locale.context';
import Booking from '../pages/Booking/Booking';
import Clients from '../pages/Clients/Clients';
import Dashboard from '../pages/Dashboard/Dashboard';
import EditTableMap from '../pages/EditTableMap/EditTableMap';
import EditShift from '../pages/Journal/EditShift';
import Journal from '../pages/Journal/Journal';
import KitchenScreen from '../pages/Kitchen/KitchenScreen';
import LegalEntities from '../pages/LegalEntities/LegalEntities';
import CategoriesList from '../pages/Menu/Categories/CategoriesList';
import Dishes from '../pages/Menu/Dishes/DishesList';
import SectionsList from '../pages/Menu/Sections/SectionsList';
import OrderArchive from '../pages/OrderArchive/OrderArchive';
import POS from '../pages/POS/POS';
import PosShiftArchive from '../pages/PosShiftArchive/PosShiftArchive';
import ProductList from '../pages/Products/ProductList';
import Promotions from '../pages/Promotions/Promotions';
import Reports from '../pages/Reports/Reports';
import Reviews from '../pages/Reviews/Reviews';
import Salary from '../pages/Salary/Salary';
import SelfOrderPoints from '../pages/SelfOrderPoints/SelfOrderPoints';
import SemiProducts from '../pages/SemiProducts/SemiProductList';
import Settings from '../pages/Settings/Settings';
import ResiduesList from '../pages/Stores/ResiduesList';
import StoresList from '../pages/Stores/StoresList';
import SupplyList from '../pages/Supply/SupplyList';
import WalletsList from '../pages/Wallets/WalletsList';
import WorkersList from '../pages/Workers/WorkersList';
import AccessDenied from './AccessDenied';
import AdminLayout from './AdminLayout';
import { LitePage, PlusPage, ProPage } from './PlanCheck/PlanCheckPage';
import PrimaryButton from './PrimaryButton';

const AdminRouter = () => {
  const { shifts, worker } = useCafe();
  const { cafe } = useAdmin();
  const { lang } = useLocale();
  const [createShift, setCreateShift] = useState(false);

  const shift = useMemo(() => {
    const time = new Date().getTime();

    return shifts.find(
      (shift) => shift.workerId === worker?.id && shift.start - 1800000 < time && shift.end + 1800000 > time
    );
  }, [shifts, worker?.id]);

  if (!worker) {
    return <AccessDenied />;
  }

  if (!shift && !worker.credentials.access_without_shift) {
    return (
      <AdminLayout>
        <Typography variant="h6" sx={{ textAlign: 'center', m: 3, color: (t) => t.palette.text.secondary }}>
          {t.no_shift[lang]}
        </Typography>

        <Box sx={{ textAlign: 'center' }}>
          <PrimaryButton
            variant="contained"
            onClick={() => setCreateShift(true)}
            disabled={!worker.credentials.allow_create_shift_on_login}
          >
            {t.add_shift[lang]}
          </PrimaryButton>
        </Box>

        {createShift && (
          <EditShift open onClose={() => setCreateShift(false)} initialWorkerId={worker?.id} fastCreate />
        )}
      </AdminLayout>
    );
  }

  const {
    pos_terminal,
    kitchen_screen,
    booking,
    dashboard,
    order_archive,
    pos_shift_archive,
    stores,
    journal,
    workers,
    wallets,
    salary,
    menu_and_product_settings,
    restaurant_settings,
    reports,
    promotions,
    clients,
    legal_entities,
    self_order_points,
  } = worker.credentials;

  return (
    <AdminLayout>
      <Routes>
        <Route path="/" element={dashboard ? <Dashboard /> : <AccessDenied />} />

        <Route
          path="products"
          element={
            menu_and_product_settings ? (
              <ProPage>
                <ProductList />
              </ProPage>
            ) : (
              <AccessDenied />
            )
          }
        />
        <Route
          path="semifinished"
          element={
            menu_and_product_settings ? (
              <ProPage>
                <SemiProducts />
              </ProPage>
            ) : (
              <AccessDenied />
            )
          }
        />
        <Route path="workers" element={workers ? <WorkersList /> : <AccessDenied />} />
        <Route
          path="wallets"
          element={
            wallets ? (
              <LitePage>
                <WalletsList />
              </LitePage>
            ) : (
              <AccessDenied />
            )
          }
        />
        <Route
          path="journal"
          element={
            journal ? (
              <ProPage>
                <Journal />
              </ProPage>
            ) : (
              <AccessDenied />
            )
          }
        />
        <Route path="editTableMap" element={restaurant_settings ? <EditTableMap /> : <AccessDenied />} />
        <Route
          path="kitchen"
          element={
            kitchen_screen ? (
              <PlusPage>
                <KitchenScreen />
              </PlusPage>
            ) : (
              <AccessDenied />
            )
          }
        />
        <Route
          path="supply"
          element={
            stores ? (
              <ProPage>
                <SupplyList />
              </ProPage>
            ) : (
              <AccessDenied />
            )
          }
        />
        <Route
          path="orderArchive"
          element={
            order_archive ? (
              <PlusPage>
                <OrderArchive />
              </PlusPage>
            ) : (
              <AccessDenied />
            )
          }
        />
        <Route
          path="shiftsArchive"
          element={
            pos_shift_archive ? (
              <PlusPage>
                <PosShiftArchive />
              </PlusPage>
            ) : (
              <AccessDenied />
            )
          }
        />
        <Route
          path="promotions"
          element={
            promotions ? (
              <ProPage>
                <Promotions />
              </ProPage>
            ) : (
              <AccessDenied />
            )
          }
        />

        <Route
          path="clients"
          element={
            clients ? (
              <ProPage>
                <Clients />
              </ProPage>
            ) : (
              <AccessDenied />
            )
          }
        />

        <Route
          path="salary"
          element={
            salary ? (
              <ProPage>
                <Salary />
              </ProPage>
            ) : (
              <AccessDenied />
            )
          }
        />
        <Route path="restaurant" element={restaurant_settings ? <Settings /> : <AccessDenied />} />
        <Route
          path="pos/*"
          element={
            pos_terminal ? (
              <LitePage>
                <POS />
              </LitePage>
            ) : (
              <AccessDenied />
            )
          }
        />
        <Route
          path="booking"
          element={
            booking ? (
              <PlusPage>
                <Booking />
              </PlusPage>
            ) : (
              <AccessDenied />
            )
          }
        />

        <Route
          path="reviews"
          element={
            booking ? (
              <LitePage>
                <Reviews />
              </LitePage>
            ) : (
              <AccessDenied />
            )
          }
        />

        <Route
          path="reports"
          element={
            reports ? (
              <PlusPage>
                <Reports />
              </PlusPage>
            ) : (
              <AccessDenied />
            )
          }
        />

        <Route
          path="legal_entities"
          element={
            legal_entities ? (
              <ProPage>
                <LegalEntities />
              </ProPage>
            ) : (
              <AccessDenied />
            )
          }
        />

        <Route
          path="self_order_points"
          element={
            self_order_points && cafe?.type === 'selfOrderPoins' ? (
              <ProPage>
                <SelfOrderPoints />
              </ProPage>
            ) : (
              <AccessDenied />
            )
          }
        />

        <Route path="menu">
          <Route index element={menu_and_product_settings ? <SectionsList /> : <AccessDenied />} />
          <Route path=":sectionId">
            <Route index element={menu_and_product_settings ? <CategoriesList /> : <AccessDenied />} />
            <Route path=":categoryId" element={menu_and_product_settings ? <Dishes /> : <AccessDenied />} />
          </Route>
        </Route>
        <Route path="stores">
          <Route
            index
            element={
              stores ? (
                <ProPage>
                  <StoresList />
                </ProPage>
              ) : (
                <AccessDenied />
              )
            }
          />
          <Route
            path=":storeId"
            element={
              stores ? (
                <ProPage>
                  <ResiduesList />
                </ProPage>
              ) : (
                <AccessDenied />
              )
            }
          />
        </Route>
      </Routes>
    </AdminLayout>
  );
};

export default AdminRouter;
